import React from "react";
import { Box, Typography, LinearProgress, Fade, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * Progressive tracker for PCB quick cost.
 * Mirrors the F&B experience with a compact progress bar and active-step message.
 */
const ProgressiveAnalysisTracker = ({ steps, overallProgress, currentStepNumber }) => {
  const totalSteps = steps.length || 1;
  const completedSteps = steps.filter((step) => step.status === "completed").length;
  const calculatedProgress = totalSteps ? (completedSteps / totalSteps) * 100 : 0;

  let currentStep;
  if (currentStepNumber && steps[currentStepNumber - 1]) {
    currentStep = steps[currentStepNumber - 1];
  } else {
    currentStep =
      steps.find((step) => step.status === "in-progress") ||
      steps.filter((step) => step.status === "completed").pop() ||
      steps.find((step) => step.status === "pending") ||
      steps[0];
  }

  const isComplete = calculatedProgress >= 100;

  return (
    <Box
      sx={{
        width: 500,
        margin: "auto",
        bgcolor: "background.paper",
        borderRadius: 4,
        p: 4,
        boxShadow: 24,
        minHeight: 200,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
            {isComplete ? "Analysis Complete!" : "Preparing Cost Estimate..."}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
            {Math.round(calculatedProgress || overallProgress || 0)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={calculatedProgress || overallProgress || 0}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: "action.disabledBackground",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              bgcolor: isComplete ? "success.main" : "primary.main",
              transition: "transform 0.4s ease-in-out, background-color 0.3s ease",
            },
          }}
        />
      </Box>

      <Fade in={true} timeout={500} key={currentStep?.id}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 2,
            px: 3,
          }}
        >
          {isComplete ? (
            <>
              <CheckCircleIcon sx={{ color: "success.main", fontSize: 24 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "success.main",
                  fontStyle: "italic",
                }}
              >
                Analysis Complete!
              </Typography>
            </>
          ) : (
            <>
              <CircularProgress size={20} sx={{ color: "text.disabled" }} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.disabled",
                  fontStyle: "italic",
                }}
              >
                {currentStep?.label || "Initializing..."}
              </Typography>
            </>
          )}
        </Box>
      </Fade>
    </Box>
  );
};

export default ProgressiveAnalysisTracker;
